import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ListFilterConfig, ListFilterValue } from "../../../components/list-filter/models";
import PageTitle from "../../../components/page-title/PageTitle";
import { ReportDataType, ReportsData, ReportsDataCompany } from "../models";
import ReportsContainer from "../ReportsContainer";
import agent from "../../../app/api/agent";
import { ReportFilterContext } from "../useReportFilter";

const dataResourceFn = async (filter?: ListFilterValue): Promise<ReportsData[]> => {
  const params: { years?: number[]; grade?: string[]; gender?: string[]; companyIds?: number[] } = {};
  if (Array.isArray(filter?.years) && filter?.years.length) {
    params.years = filter.years.map(getValue);
  }
  if (Array.isArray(filter?.grade) && filter?.grade.length) {
    params.grade = filter.grade.map(getValue);
  }
  if (Array.isArray(filter?.gender) && filter?.gender.length) {
    params.gender = filter.gender.map(getValue);
  }
  if (Array.isArray(filter?.company) && filter?.company.length) {
    params.companyIds = filter.company.map(getValue);
  }

  const response = await agent.Reports.schoolStats(params);

  const data: ReportsData[] = [
    {
      title: "reports.reflection.total",
      type: ReportDataType.Count,
      value: [
        {
          unit: "reports.reflection.totalRegisteredStudentsCount",
          value: response.totalRegisteredStudentsCount,
        },
        {
          unit: "reports.reflection.applicationCount",
          value: response.applicationCount,
        },
        {
          unit: "reports.reflection.visitsCount",
          value: response.visitsCount,
        },
        {
          unit: "reports.reflection.finishedProgramCount",
          value: response.finishedProgramCount,
        },
      ],
    },
  ];

  if (response.bestCompanies?.length > 0) {
    const bestCompaniesSection: ReportsDataCompany = {
      title: "reports.reflection.bestCompaniesSchool",
      type: ReportDataType.Company,
      value: response.bestCompanies.map((company) => ({
        id: company.id,
        title: company.companyTitle,
        logoUrl: company.companyLogoUrl,
        value: company.visitsCount,
      })),
    };
    data.push(bestCompaniesSection);
  }

  if (response.bestCompaniesNationwide?.length > 0) {
    const bestNationwideSection: ReportsDataCompany = {
      title: "reports.reflection.bestCompaniesNationwide",
      type: ReportDataType.Company,
      value: response.bestCompaniesNationwide.map((company) => ({
        id: company.id,
        title: company.companyTitle,
        logoUrl: company.companyLogoUrl,
        value: company.visitsCount,
      })),
    };
    data.push(bestNationwideSection);
  }

  if (response.mostActiveGrades?.length > 0) {
    data.push({
      title: "reports.reflection.mostActiveGrades",
      type: ReportDataType.Top,
      value: response.mostActiveGrades.map((item) => ({
        title: `${item.schoolName ? item.schoolName : ""} ${item.grade}`,
        value: item.count,
      })),
    });
  }

  return data;
};

const getValue = <T extends { value: any }>(x: T) => x.value;

const SchoolReportsStatistics = () => {
  const { t } = useTranslation();

  const { years, grades, genders, companies } = useContext(ReportFilterContext);
  const filters: ListFilterConfig = useMemo(
    () => [years, companies, grades, genders],
    [years, grades, genders, companies]
  );

  return (
    <>
      <PageTitle title={t("reports.statistics")} />
      <ReportsContainer dataResourceFn={dataResourceFn} filterConfig={filters} />
    </>
  );
};

export default SchoolReportsStatistics;
